import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { motion, type MotionStyle } from "framer-motion";
import { Sparkles } from "lucide-react";
import {
  color,
  motion as motionTokens,
  radius,
  space,
  usePrefersReducedMotion,
} from "@tokens/index";
import { Button } from "@primitives/Button";
import { ChoiceChips, type ChoiceChipsOption } from "@primitives/index";
import { Dialog } from "@primitives/overlay";
import { Text } from "@primitives/Text";
import { TextInput } from "@primitives/TextInput";
import { useViewport } from "@primitives/viewport";
import { EntitySearchField } from "./EntitySearchField";
import { humanizeSlug } from "./humanize";
import {
  CUSTOM_ENTITY_TYPE,
  KNOWN_ENTITY_TYPES,
  type CreateWorkflowDialogProps,
  type EntityCandidate,
  type ExistingEntityWorkflow,
} from "./types";

/**
 * New workflow dialog.
 *
 * Dense form grammar: a normal-weight label above each control with a 4px
 * gap, 12px between rows. Entity type and Kind are `ChoiceChips`, so the
 * groups keep their shape however many kinds the registry returns.
 *
 * Keyboard: Enter in any single-line field submits when the form is valid;
 * Ctrl/Cmd+Enter submits from anywhere in the dialog. The entity combobox
 * consumes Enter first when its list has options. Switching to "Other…"
 * fades the entity block in and moves focus to the first field of the new
 * mode.
 */

const FOLLOW_UP_KIND = "follow-up";

function preferredKind(availableKinds: readonly string[]): string {
  return availableKinds.includes(FOLLOW_UP_KIND) ? FOLLOW_UP_KIND : (availableKinds[0] ?? "");
}

const ENTITY_TYPE_OPTIONS: readonly ChoiceChipsOption<string>[] = [
  ...KNOWN_ENTITY_TYPES.map((value) => ({ value, label: humanizeSlug(value) })),
  { value: CUSTOM_ENTITY_TYPE, label: "Other\u2026" },
];

/**
 * Column count for a chip group so its last row is full whenever the count
 * allows: 3 when divisible by three, else 2 when even, else 3 (the smaller
 * orphan). Compact viewports take 2 so long labels keep their words.
 */
function chipColumns(count: number, compact: boolean): "auto" | number {
  if (count <= 1) return "auto";
  if (compact) return 2;
  if (count % 3 === 0) return 3;
  if (count % 2 === 0) return 2;
  return 3;
}

const formStyle: CSSProperties = {
  display: "grid",
  gap: space[3],
};

// The entity block swaps modes inside a motion element; MotionStyle rejects
// CSSProperties under exactOptionalPropertyTypes, so it gets its own literal.
const swapStyle: MotionStyle = {
  display: "grid",
  gap: space[3],
};

const fieldStyle: CSSProperties = {
  display: "grid",
  gap: space[1],
  minWidth: 0,
};

const errorStyle: CSSProperties = {
  padding: `${space[3]} ${space[4]}`,
  borderRadius: radius.md,
  background: color.status.dangerBg,
};

interface FieldProps {
  readonly label: string;
  readonly htmlFor?: string;
  readonly id?: string;
  readonly children: ReactNode;
}

/** Label above control. `htmlFor` renders a `<label>`; `id` renders a span for `aria-labelledby`. */
function Field({ label, htmlFor, id, children }: FieldProps): ReactElement {
  return (
    <div style={fieldStyle}>
      {htmlFor !== undefined ? (
        <Text as="label" role="bodySm" weight="medium" tone="muted" htmlFor={htmlFor}>
          {label}
        </Text>
      ) : (
        <Text
          as="span"
          role="bodySm"
          weight="medium"
          tone="muted"
          {...(id !== undefined ? { id } : {})}
        >
          {label}
        </Text>
      )}
      {children}
    </div>
  );
}

const isSingleLineInput = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement;

export function CreateWorkflowDialog({
  isOpen,
  isSubmitting,
  errorMessage,
  availableKinds,
  onClose,
  onSubmit,
  entityCandidates,
  entityCandidatesLoading,
  onEntitySearch,
  entityResolveStatus,
  entityResolveLoading,
  onEntityResolve,
  existingWorkflows = [],
  existingWorkflowsLoading = false,
  onOpenExisting,
}: CreateWorkflowDialogProps): ReactElement {
  const { isCompact } = useViewport();
  const reduced = usePrefersReducedMotion();
  const baseId = useId();
  const entityTypeLabelId = `${baseId}-entity-type-label`;
  const kindLabelId = `${baseId}-kind-label`;
  const titleId = `${baseId}-title`;
  const customTypeId = `${baseId}-custom-type`;

  const [entityType, setEntityType] = useState<string>(KNOWN_ENTITY_TYPES[0]);
  const [customEntityType, setCustomEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [entityDisplayName, setEntityDisplayName] = useState("");
  const [kind, setKind] = useState("");
  const [title, setTitle] = useState("");
  const [selectedEntity, setSelectedEntity] = useState<EntityCandidate | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const customTypeInputRef = useRef<HTMLInputElement>(null);
  const isCustomEntity = entityType === CUSTOM_ENTITY_TYPE;

  // Follow-up is the default when the registry has it: a human-opened
  // entity thread. Derived, not stored: an empty draft resolves the
  // moment kinds arrive, and a refetch never clobbers a still-valid pick.
  const effectiveKind = availableKinds.includes(kind) ? kind : preferredKind(availableKinds);

  useEffect(() => {
    if (!isOpen) return;
    setEntityType(KNOWN_ENTITY_TYPES[0]);
    setCustomEntityType("");
    setEntityId("");
    setEntityDisplayName("");
    setKind("");
    setTitle("");
    setSelectedEntity(null);
  }, [isOpen]);

  // Mode swap moves focus to the first editable field of the new mode. The
  // ref tracks the last mode that was focused so the open-reset does not
  // fight the dialog's own initial focus.
  const focusedModeRef = useRef(isCustomEntity);
  useEffect(() => {
    if (!isOpen || focusedModeRef.current === isCustomEntity) return;
    focusedModeRef.current = isCustomEntity;
    const target = isCustomEntity ? customTypeInputRef.current : searchInputRef.current;
    target?.focus({ preventScroll: true });
  }, [isOpen, isCustomEntity]);

  const handleEntityTypeChange = useCallback(
    (next: string) => {
      setEntityType(next);
      setSelectedEntity(null);
      setEntityId("");
      setEntityDisplayName("");
      onEntityResolve("", "");
    },
    [onEntityResolve],
  );

  const selectCandidate = useCallback(
    (candidate: EntityCandidate) => {
      setSelectedEntity(candidate);
      setEntityId(candidate.id);
      setEntityDisplayName(candidate.displayName);
      onEntityResolve(candidate.type, candidate.id);
    },
    [onEntityResolve],
  );

  const clearSelectedEntity = useCallback(() => {
    setSelectedEntity(null);
    setEntityId("");
    setEntityDisplayName("");
    onEntityResolve("", "");
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [onEntityResolve]);

  const openExisting = useCallback(
    (workflow: ExistingEntityWorkflow) => {
      onOpenExisting?.(workflow);
    },
    [onOpenExisting],
  );

  // Title is optional: the BFF derives a default when it is blank.
  const canSubmit = useMemo(() => {
    if (isSubmitting) return false;
    if (effectiveKind.length === 0) return false;
    if (isCustomEntity) {
      return (
        customEntityType.trim().length > 0 &&
        entityId.trim().length > 0 &&
        entityDisplayName.trim().length > 0
      );
    }
    return selectedEntity !== null;
  }, [
    isSubmitting,
    effectiveKind,
    isCustomEntity,
    customEntityType,
    entityId,
    entityDisplayName,
    selectedEntity,
  ]);

  const resolveMessage = useMemo((): string | null => {
    if (entityResolveLoading) return "Verifying entity\u2026";
    if (entityResolveStatus === "NotFound") return "Entity not found.";
    if (entityResolveStatus === "Unavailable") return "Entity is currently unavailable.";
    if (entityResolveStatus === "UnsupportedType") return "Entity type is not supported.";
    return null;
  }, [entityResolveStatus, entityResolveLoading]);

  const submit = useCallback(() => {
    if (!canSubmit) return;
    if (isCustomEntity) {
      onSubmit({
        workflowId: crypto.randomUUID(),
        entityType: customEntityType.trim(),
        entityId: entityId.trim(),
        entityDisplayName: entityDisplayName.trim(),
        kind: effectiveKind,
        title: title.trim(),
      });
    } else if (selectedEntity !== null) {
      onSubmit({
        workflowId: crypto.randomUUID(),
        entityType: selectedEntity.type,
        entityId: selectedEntity.id,
        entityDisplayName: selectedEntity.displayName,
        kind: effectiveKind,
        title: title.trim(),
      });
    }
  }, [
    canSubmit,
    isCustomEntity,
    customEntityType,
    entityId,
    entityDisplayName,
    effectiveKind,
    title,
    onSubmit,
    selectedEntity,
  ]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submit();
    },
    [submit],
  );

  // Enter is the happy path: a single-line field submits when valid. The
  // combobox has already consumed Enter (defaultPrevented) when it picked a
  // candidate. Ctrl/Cmd+Enter submits from any element.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter") return;
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        submit();
        return;
      }
      if (event.defaultPrevented || !isSingleLineInput(event.target)) return;
      event.preventDefault();
      submit();
    },
    [submit],
  );

  const kindOptions = useMemo<readonly ChoiceChipsOption<string>[]>(
    () => availableKinds.map((value) => ({ value, label: humanizeSlug(value) })),
    [availableKinds],
  );

  const swapTransition = useMemo(
    () => ({ duration: reduced ? 0 : 0.12, ease: motionTokens.curves.standard }),
    [reduced],
  );

  const footerStyle: CSSProperties = isCompact
    ? { display: "flex", flexDirection: "column-reverse", gap: space[2], width: "100%" }
    : { display: "flex", flexDirection: "row", gap: space[2], marginLeft: "auto" };

  const footer = (
    <div style={footerStyle} onKeyDown={handleKeyDown}>
      <Button
        type="button"
        variant="ghost"
        size="md"
        onClick={onClose}
        disabled={isSubmitting}
        fullWidth={isCompact}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        variant="primary"
        size="md"
        busy={isSubmitting}
        disabled={!canSubmit && !isSubmitting}
        fullWidth={isCompact}
      >
        Create workflow
      </Button>
    </div>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="New workflow"
      description="Anchor a thread of work to a business entity."
      icon={<Sparkles size={20} strokeWidth={2} />}
      size="md"
      presentation="auto"
      initialFocusRef={searchInputRef}
      as="form"
      onSubmit={handleFormSubmit}
      footer={footer}
      testId="create-workflow-dialog"
    >
      <div onKeyDown={handleKeyDown} style={formStyle}>
        <Field label="Entity type" id={entityTypeLabelId}>
          <ChoiceChips
            labelledBy={entityTypeLabelId}
            options={ENTITY_TYPE_OPTIONS}
            value={entityType}
            onChange={handleEntityTypeChange}
            columns={chipColumns(ENTITY_TYPE_OPTIONS.length, isCompact)}
          />
        </Field>

        <motion.div
          key={isCustomEntity ? "custom" : "search"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={swapTransition}
          style={swapStyle}
        >
          {isCustomEntity ? (
            <>
              <Field label="Custom entity type" htmlFor={customTypeId}>
                <TextInput
                  id={customTypeId}
                  ref={customTypeInputRef}
                  placeholder="e.g. facility-visit"
                  value={customEntityType}
                  onChange={(event) => {
                    setCustomEntityType(event.target.value);
                  }}
                  fullWidth
                  required
                />
              </Field>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr",
                  gap: space[3],
                }}
              >
                <Field label="Entity ID" htmlFor={`${baseId}-entity-id`}>
                  <TextInput
                    id={`${baseId}-entity-id`}
                    placeholder="e.g. LD-1042"
                    value={entityId}
                    onChange={(event) => {
                      setEntityId(event.target.value);
                    }}
                    fullWidth
                    required
                  />
                </Field>
                <Field label="Display name" htmlFor={`${baseId}-entity-name`}>
                  <TextInput
                    id={`${baseId}-entity-name`}
                    placeholder={"e.g. Acme \u00b7 Phoenix to Dallas"}
                    value={entityDisplayName}
                    onChange={(event) => {
                      setEntityDisplayName(event.target.value);
                    }}
                    fullWidth
                    required
                  />
                </Field>
              </div>
            </>
          ) : (
            <EntitySearchField
              key={entityType}
              entityType={entityType}
              inputRef={searchInputRef}
              selected={selectedEntity}
              candidates={entityCandidates}
              candidatesLoading={entityCandidatesLoading}
              message={resolveMessage}
              messageTone={entityResolveLoading ? "muted" : "danger"}
              onSearch={onEntitySearch}
              onSelect={selectCandidate}
              onClear={clearSelectedEntity}
            />
          )}
        </motion.div>

        {existingWorkflowsLoading && existingWorkflows.length === 0 ? (
          <Text role="bodySm" tone="muted">
            Looking for existing threads…
          </Text>
        ) : null}

        {existingWorkflows.length > 0 && onOpenExisting !== undefined ? (
          <Field label="Existing threads" id={`${baseId}-existing-label`}>
            <ul
              aria-labelledby={`${baseId}-existing-label`}
              data-testid="existing-entity-workflows"
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "grid",
                gap: space[1],
              }}
            >
              {existingWorkflows.map((workflow) => (
                <li
                  key={workflow.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: space[2],
                    minWidth: 0,
                    padding: `${space[1]} ${space[2]}`,
                    borderRadius: radius.md,
                    background: color.bg.surface2,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: "grid", gap: space[1] }}>
                    <Text
                      role="bodySm"
                      weight="medium"
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {workflow.title.length > 0 ? workflow.title : "Untitled"}
                    </Text>
                    <Text role="caps" size="xs" tone="muted">
                      {humanizeSlug(workflow.kind)}
                    </Text>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openExisting(workflow)}
                  >
                    Open
                  </Button>
                </li>
              ))}
            </ul>
          </Field>
        ) : null}

        <Field label="Kind" id={kindLabelId}>
          <ChoiceChips
            labelledBy={kindLabelId}
            options={kindOptions}
            value={effectiveKind.length > 0 ? effectiveKind : null}
            onChange={setKind}
            columns={chipColumns(kindOptions.length, isCompact)}
          />
        </Field>

        <Field label="Title (optional)" htmlFor={titleId}>
          <TextInput
            id={titleId}
            placeholder={
              entityDisplayName.trim().length > 0
                ? `Defaults to "${entityDisplayName.trim()}"`
                : "What is this workflow about?"
            }
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
            fullWidth
          />
        </Field>

        {errorMessage !== null ? (
          <div role="alert" style={errorStyle}>
            <Text role="bodySm" tone="danger">
              {errorMessage}
            </Text>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
